import { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Crosshair, MapPin } from "lucide-react";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import MapClickHandler from "@/components/MapClickHandler";
import FitToPoint from "@/components/FitToPoint";
import Section from "@/components/Section";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

export default function LocationEditor ({ location_name, location_lat, location_lng, update }) {
	const { t } = useTranslation();

	const [geoLoading, setGeoLoading] = useState(false);
	const [geoActive, setGeoActive] = useState(false);

	const activateGeoSearch = () => {
		if (!navigator.geolocation) return;
		setGeoLoading(true);
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				update('location_lat', lat);
				update('location_lng', lng);
				setGeoActive(true);
				setGeoLoading(false);
			},
			() => { setGeoLoading(false); },
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	};

	return (
		<Section title={ t('horse.location_section') }>
			<div className="mb-4">
				<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
					{ t('horse.location') } *
				</Label>
				<Input required data-testid="horse-location" value={ location_name }
							 onChange={ e => update('location_name', e.target.value) }
							 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
							 placeholder={ t('horse.location_placeholder') }/>
			</div>
			<p className="text-sm text-[#666055] mb-3 flex items-center gap-1"><MapPin className="w-4 h-4"/>
				{ t('horse.click_map') }
			</p>
			<div className="h-[300px] rounded-sm overflow-hidden border border-[#E6E2D6]">
				<MapContainer center={ [location_lat || 38.7223, location_lng || -9.1393] }
											zoom={ 6 } style={ { height: '100%', width: '100%' } }>
					<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
					{ location_lat && location_lng &&
						<Marker position={ [parseFloat(location_lat), parseFloat(location_lng)] }/>
					}
					<MapClickHandler
						onLocationSelect={ (lat, lng) => {
							update('location_lat', lat);
							update('location_lng', lng);
						} }/>
					<FitToPoint lat={ location_lat || 38.7223 } lng={ location_lng || -9.1393 } radius={ 500000 }/>
				</MapContainer>
			</div>
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
				<div>
					<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
						{ t('horse.latitude') } *
					</Label>
					<Input required type="number" step="any" value={ location_lat }
								 onChange={ e => update('location_lat', e.target.value) }
								 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 text-sm"/>
				</div>
				<div>
					<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
						{ t('horse.longitude') } *
					</Label>
					<Input required type="number" step="any" value={ location_lng }
								 onChange={ e => update('location_lng', e.target.value) }
								 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 text-sm"/>
				</div>
				<Button onClick={ activateGeoSearch } disabled={ geoLoading } variant="outline"
								className={`w-full h-10 col-span-2 md:col-span-1 rounded-sm text-sm md:mt-7 flex items-center gap-2 ${geoActive ? 'bg-[#1A3C34] text-white ' +
									'border-[#1A3C34] hover:bg-[#153029]' : 'border-[#E6E2D6] text-[#1A3C34] hover:bg-[#F2F0E9]'}`}>
					<Crosshair className="w-4 h-4" />
					{ geoLoading ?
						t('common.loading') : t('search.use_my_location')
					}
				</Button>
			</div>
		</Section>
	)
}